import { useId, useState } from 'react';
import type { WorkLog } from '@aegislab/shared';
import { Button, Textarea } from '@/shared/ui/atoms';
import { formatMonthDayKo, formatTimeShort } from '@/shared/lib/format';
import { getApiErrorMessage } from '@/shared/lib/getApiErrorMessage';
import { extractFreeMemo, replaceFreeMemo } from '../../lib/helpers';
import { groupByQuest, taskStatusLabel, visibleTasks } from './historyEntries';
import * as s from './WorklogHistoryReader.css';

interface Props {
  readonly log: WorkLog;
  /** 검색어 — 메모가 안 맞으면 맞는 태스크만 보인다. */
  readonly query: string;
  readonly onUpdateMemo: (workLogId: string, content: string) => Promise<unknown>;
}

/**
 * 지난 일지 하루 — 메모와 그 날 태스크를 읽고, 메모는 「저장」을 누를 때만 저장한다.
 * 자동저장을 쓰지 않는 이유: 마감한 일지는 저장마다 정정 이력이 1건씩 쌓여 팀장 화면의
 * 「정정 N회」가 타이핑 횟수가 된다(조사 문서 §4 함정 2).
 */
export function WorklogHistoryDay({ log, query, onUpdateMemo }: Props) {
  const headingId = useId();
  const memo = extractFreeMemo(log.content);
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dayLabel = formatMonthDayKo(log.logDate);
  const tasks = visibleTasks(log, query);

  function save() {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    onUpdateMemo(log.id, replaceFreeMemo(log.content, draft))
      .then(() => setDraft(null))
      .catch((e: unknown) => setError(getApiErrorMessage(e, '메모를 저장하지 못했습니다.')))
      .finally(() => setSaving(false));
  }

  return (
    <article className={s.entry} aria-labelledby={headingId} data-log-date={log.logDate}>
      <h3 className={s.date} id={headingId}>
        <time dateTime={log.logDate}>
          {log.logDate.slice(0, 4)}년 {formatMonthDayKo(log.logDate, { weekday: true })}
        </time>
        <span className={s.small}>
          {log.status === 'SUBMITTED' ? '마감한 일지' : '마감하지 않은 일지'}
        </span>
      </h3>

      {draft === null ? (
        <>
          {memo ? (
            <p className={s.memo}>{memo}</p>
          ) : (
            <p className={s.memoEmpty}>이 날은 메모를 적지 않았습니다.</p>
          )}
          <Button
            size="xs"
            variant="link"
            aria-label={`${dayLabel} 메모 고치기`}
            onClick={() => setDraft(memo)}
          >
            메모 고치기
          </Button>
        </>
      ) : (
        <div className={s.context}>
          <Textarea
            label={`${dayLabel} 메모`}
            fullWidth
            rows={4}
            value={draft}
            error={error ?? undefined}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          {log.status === 'SUBMITTED' && (
            <p className={s.small}>
              마감한 일지라 저장하면 팀장님 화면에 고친 날짜가 함께 보입니다.
            </p>
          )}
          <div className={s.actions}>
            <Button size="sm" onClick={save} disabled={saving}>
              저장
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
              취소
            </Button>
          </div>
        </div>
      )}

      {groupByQuest(tasks).map((group) => (
        <div className={s.group} key={group.key}>
          <h4 className={s.groupTitle}>{group.title}</h4>
          {group.tasks.map((task) => (
            <div className={s.record} key={task.id}>
              <p className={s.recordTitle}>{task.title}</p>
              <p className={s.small}>
                {taskStatusLabel(task)} · {formatTimeShort(task.createdAt)}
              </p>
              {task.checkpointTitle && (
                <p className={s.small}>체크포인트 · {task.checkpointTitle}</p>
              )}
              {task.memo && <p className={s.recordMemo}>{task.memo}</p>}
            </div>
          ))}
        </div>
      ))}
    </article>
  );
}
